import React, { useRef } from 'react';
import Modal from '@material-ui/core/Modal';
import { Close } from '@material-ui/icons';
import Box from 'Components/Atoms/Box';
import { classHelper } from 'utils/helper';
import { EditorProduct } from './products';
import { PRODUCT_OVERLAY_PROPS } from './overlay';

export type AddableProduct = {
  product: EditorProduct;
  label: string;
  restorable: boolean;
  /** In-app page that explains and activates the product; never activates by itself. */
  destination: string | null;
};

type Props = {
  items: AddableProduct[];
  onRestore: (id: string) => void;
  /** Absent while the editor has unsaved changes. */
  onOpenDestination?: (path: string) => void;
  onClose: () => void;
};

const statusText = ({ product, restorable }: AddableProduct) => {
  if (!product.hide) return 'Ausgeblendet';
  if (product.hideLabel) return 'Demnächst verfügbar';
  return restorable ? 'Ausgeblendet · nicht aktiv' : 'Nicht aktiv';
};

/** Hidden and inactive products as an app grid. */
const AddProductsSheet = ({
  items,
  onRestore,
  onOpenDestination,
  onClose,
}: Props) => {
  const closeButton = useRef<HTMLButtonElement>(null);

  return (
    <Modal
      {...PRODUCT_OVERLAY_PROPS}
      open
      onClose={onClose}
      onRendered={() => closeButton.current?.focus()}
    >
      <div
        className="product-folder-sheet product-add-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-add-title"
        tabIndex={-1}
      >
        <div className="product-folder-header">
          <h3 id="product-add-title">Produkte hinzufügen</h3>
          <button
            ref={closeButton}
            type="button"
            className="product-overlay-round-button"
            aria-label="Hinzufügen schließen"
            onClick={onClose}
          >
            <Close fontSize="small" />
          </button>
        </div>
        {!items.length && (
          <p className="product-editor-panel-text">
            Alle Produkte werden bereits angezeigt.
          </p>
        )}
        <ul className="product-icons product-add-grid">
          {items.map((item) => (
            <li key={item.product.id} className="product-add-tile">
              <div
                className={classHelper([
                  'inline-column',
                  'product-icon',
                  item.product.hide && 'hidden',
                ])}
              >
                <Box
                  flex
                  className="center product-editor-art"
                  padding={20}
                  elevation={0}
                >
                  <img
                    height={40}
                    width={40}
                    alt=""
                    src={`/products/${item.product.id}.svg`}
                  />
                </Box>
              </div>
              <strong className="product-add-name">{item.label}</strong>
              <span className="product-add-status">{statusText(item)}</span>
              {item.restorable && (
                <button
                  type="button"
                  className="product-editor-button is-primary"
                  aria-label={`${item.label} anzeigen`}
                  onClick={() => onRestore(item.product.id)}
                >
                  Anzeigen
                </button>
              )}
              {!!item.destination &&
                (onOpenDestination ? (
                  <button
                    type="button"
                    className="product-editor-button"
                    aria-label={`${item.label}: Details und Aktivierung`}
                    onClick={() => onOpenDestination(item.destination!)}
                  >
                    Details &amp; Aktivierung
                  </button>
                ) : (
                  <span className="product-add-hint">
                    Zuerst Änderungen speichern
                  </span>
                ))}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default AddProductsSheet;
